import Grid from '../../Grid'
import { styled } from '../../../utils/styled'
import { Popper } from '@mui/material'
import typography from '../../theme/typography'
import { MENTION_CHIP_CLASS_NAME } from './utils'

export const StyledGridComposerContainer = styled(Grid)<{
  $inputHasValue: boolean
  $hasActions: boolean
  $isInternalMessage?: boolean
  $elevated?: boolean
}>(
  ({
    $inputHasValue: inputHasValue,
    $hasActions: hasActions,
    theme,
    $isInternalMessage: isInternalMessage = false,
    $elevated: elevated = false
  }) => {
    let composerBackgroundColor: string
    if (isInternalMessage) {
      composerBackgroundColor = theme.tokens.background.semantic.warning.tonal.superSoft.default
    } else if (inputHasValue) {
      composerBackgroundColor = theme.tokens.background.main.default
    } else {
      composerBackgroundColor = theme.tokens.background.tonal.soft.default
    }

    const restingBoxShadow = elevated ? theme.elevation.mediumDepth : `0 0 0 1px ${theme.tokens.border.main.tonal}`
    const focusBoxShadow = elevated
      ? `${theme.elevation.mediumDepth}, 0 0 0 4px ${theme.tokens.border.focus.brand.haloSubtle}`
      : `0 0 0 4px ${theme.tokens.border.focus.brand.haloSubtle}`

    return {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      borderRadius: '20px',
      boxShadow: restingBoxShadow,
      backgroundColor: composerBackgroundColor,
      padding: hasActions ? '12px 8px 4px 16px' : '8px 8px 8px 16px',
      outline: '1px solid transparent',
      '&:focus-within': {
        backgroundColor: composerBackgroundColor,
        boxShadow: focusBoxShadow,
        outline: `1px solid ${theme.tokens.border.focus.brand.primary}`
      }
    }
  }
)

export const StyledGridComposerActionsContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '2px'
}))

export const StyledGridComposerInputContainer = styled(Grid)(() => ({
  display: 'flex',
  width: '100%',
  gap: '16px',
  flexDirection: 'row',
  '> button': {
    flexGrow: 0
  }
}))

export const StyledComposerMarkdownPreview = styled('div')(({ theme }) => ({
  flexGrow: 1,
  position: 'relative',
  paddingTop: '8px',
  minHeight: '24px',
  outline: 'none',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  ...typography.body2,
  color: theme.tokens.foreground.main.primary,
  '&[data-empty]::before': {
    content: 'attr(data-placeholder)',
    color: theme.tokens.foreground.main.secondary,
    pointerEvents: 'none',
    position: 'absolute'
  },
  '& b, & strong': {
    fontWeight: 700
  },
  '& i, & em': {
    fontStyle: 'italic'
  },
  '& a': {
    color: theme.tokens.foreground.main.link,
    textDecoration: 'underline'
  },
  [`& .${MENTION_CHIP_CLASS_NAME}`]: {
    color: theme.tokens.foreground.main.link,
    fontWeight: 600,
    userSelect: 'none'
  }
}))

export const StyledTriggerMenuPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== 'menuWidth' && prop !== 'menuMaxHeight'
})<{ menuWidth: string; menuMaxHeight: string }>(({ theme, menuWidth, menuMaxHeight }) => ({
  zIndex: 1500,
  width: menuWidth,
  borderRadius: '8px',
  boxShadow: theme.elevation.mediumDepth,
  backgroundColor: theme.tokens.background.main.default,
  // Cap the list so a long set scrolls instead of growing past the viewport.
  maxHeight: menuMaxHeight,
  overflowY: 'auto',
  overflowX: 'hidden'
}))

export const StyledTriggerMenuList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingBlock: theme.spacing(0),
  paddingInline: 0
}))

export const StyledTriggerMenuItem = styled('li')<{
  'data-highlighted': boolean
  'data-disabled': boolean
}>(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  listStyle: 'none',
  ...typography.body2,
  '&[data-disabled="true"]': {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  '&:hover, &[data-highlighted="true"]': {
    backgroundColor: theme.tokens.background.semantic.info.tonal.superSoft.default
  },
  '&[data-disabled="true"]:hover': {
    backgroundColor: 'transparent'
  }
}))

export const StyledTriggerMenuEmpty = styled('li')(({ theme }) => ({
  padding: theme.spacing(1, 2),
  listStyle: 'none',
  ...typography.body2,
  color: theme.tokens.foreground.main.secondary
}))

export const StyledTriggerMenuItemText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1
}))

export const StyledTriggerMenuItemAnnotation = styled('span')(({ theme }) => ({
  ...typography.body3,
  color: theme.tokens.foreground.main.secondary
}))
